import { supabase } from './supabase';

/**
 * Add a track to the user's hidden tracks (removes from all playback)
 * @param userId - The user's ID
 * @param trackId - The track ID to hide
 * @returns Promise with error if operation failed
 */
export async function hideTrack(
  userId: string,
  trackId: string
): Promise<{ error: string | null }> {
  try {
    // Get current hidden tracks
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('hidden_tracks')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      return { error: fetchError.message };
    }

    // Get current hidden tracks array (default to empty array)
    const currentHidden: string[] = Array.isArray(profile?.hidden_tracks)
      ? (profile.hidden_tracks as string[])
      : [];

    // Check if track is already hidden
    if (currentHidden.includes(trackId)) {
      return { error: null }; // Already hidden, no error
    }

    // Add track to hidden list
    const updatedHidden = [...currentHidden, trackId];

    // Update profile
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ hidden_tracks: updatedHidden })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating hidden tracks:', updateError);
      return { error: updateError.message };
    }

    return { error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to hide track';
    console.error('Unexpected error hiding track:', err);
    return { error: errorMessage };
  }
}

/**
 * Remove a track from the user's hidden tracks (restore to playback)
 * @param userId - The user's ID
 * @param trackId - The track ID to unhide
 * @returns Promise with error if operation failed
 */
export async function unhideTrack(
  userId: string,
  trackId: string
): Promise<{ error: string | null }> {
  try {
    // Get current hidden tracks
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('hidden_tracks')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      return { error: fetchError.message };
    }

    // Get current hidden tracks array (default to empty array)
    const currentHidden: string[] = Array.isArray(profile?.hidden_tracks)
      ? (profile.hidden_tracks as string[])
      : [];

    // Remove track from hidden list
    const updatedHidden = currentHidden.filter((id) => id !== trackId);

    // Update profile
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ hidden_tracks: updatedHidden })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating hidden tracks:', updateError);
      return { error: updateError.message };
    }

    return { error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to unhide track';
    console.error('Unexpected error unhiding track:', err);
    return { error: errorMessage };
  }
}

/**
 * Check if a track is hidden
 * @param userId - The user's ID
 * @param trackId - The track ID to check
 * @returns Promise with boolean indicating if track is hidden
 */
export async function isTrackHidden(
  userId: string,
  trackId: string
): Promise<{ isHidden: boolean; error: string | null }> {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('hidden_tracks')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return { isHidden: false, error: error.message };
    }

    const hidden: string[] = Array.isArray(profile?.hidden_tracks)
      ? (profile.hidden_tracks as string[])
      : [];

    return { isHidden: hidden.includes(trackId), error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to check hidden tracks';
    console.error('Unexpected error checking hidden tracks:', err);
    return { isHidden: false, error: errorMessage };
  }
}

/**
 * Get all hidden track IDs for a user
 * @param userId - The user's ID
 * @returns Promise with array of track IDs
 */
export async function getHiddenTracks(
  userId: string
): Promise<{ tracks: string[]; error: string | null }> {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('hidden_tracks')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return { tracks: [], error: error.message };
    }

    const tracks: string[] = Array.isArray(profile?.hidden_tracks)
      ? (profile.hidden_tracks as string[])
      : [];

    return { tracks, error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to get hidden tracks';
    console.error('Unexpected error getting hidden tracks:', err);
    return { tracks: [], error: errorMessage };
  }
}
